import type { PublicVesselGroup, PublicVesselSummary } from "../../shared/contracts";
import { RIVER_MOUTH_S_METERS, routeLocation, inboundRouteTangent } from "../../shared/river-model";
import { separateRouteHulls } from "../../shared/route-hulls";
import { projectedCorridorProgress, type VesselMotionFrame } from "./vessel-motion";

// Circumscribed footprints include the 1.18 selection enlargement and a water
// gap. This protects the hull even while its heading eases through a bend.
export function vesselDisplayRadius(type: PublicVesselSummary["type"]): number {
  const length = type === "cargo" || type === "tanker" ? 3.2 : type === "passenger" ? 2.85 :
    type === "yacht" ? 2.55 : type === "sailboat" ? 2.15 : type === "fishing" ? 2.1 : 2.05;
  return Math.hypot(length, 0.85) * 1.18 / 2 + 0.15;
}

export function displayRouteBounds(vessel: PublicVesselSummary): { minimum: number; maximum: number } {
  const s = vessel.sMeters;
  // Layout can separate symbols but cannot move an unobserved vessel through
  // Brickell or choose an unobserved branch at the river mouth.
  if (s < RIVER_MOUTH_S_METERS) return { minimum: s - 750, maximum: RIVER_MOUTH_S_METERS - 2 };
  if (s < 0) return { minimum: RIVER_MOUTH_S_METERS + 2, maximum: -2 };
  return { minimum: 0, maximum: s + 750 };
}

/** A cached relationship expires as a unit, including during a disconnected session. */
export function currentTowGroups(vessels: readonly PublicVesselSummary[], groups: readonly PublicVesselGroup[], nowMs: number): PublicVesselGroup[] {
  const byId = new Map(vessels.map((vessel) => [vessel.id, vessel]));
  return groups.filter((group) => [...group.tugIds, ...group.towIds].every((id) => {
    const vessel = byId.get(id);
    const age = vessel ? nowMs - Date.parse(vessel.observedAt) : NaN;
    return Number.isFinite(age) && age >= -30_000 && age <= 360_000;
  }));
}

/** Rendering state only. Neither reported coordinates nor timestamps are rewritten. */
export class VesselFormationController {
  private readonly orders = new Map<string, string[]>();

  layout(frames: readonly VesselMotionFrame[], vessels: readonly PublicVesselSummary[],
    groups: readonly PublicVesselGroup[], nowMs: number): VesselMotionFrame[] {
    const byId = new Map(vessels.map((vessel) => [vessel.id, vessel]));
    const byFrame = new Map(frames.map((frame) => [frame.id, frame]));
    const desired = new Map(frames.map((frame) => [frame.id, frame.renderedSMeters]));
    const membership = new Map<string, { formation: string; order: number; idle: boolean }>();
    const active = new Set<string>();
    for (const group of currentTowGroups(vessels, groups, nowMs)) {
      const members = [...group.tugIds, ...group.towIds].map((id) => byId.get(id));
      if (members.some((member) => !member || !byFrame.has(member.id) ||
          nowMs - Date.parse(member.observedAt) > 360_000 || Date.parse(member.observedAt) > nowMs + 30_000)) continue;
      const present = members as PublicVesselSummary[];
      const newest = present.reduce((a, b) => a.observedAt >= b.observedAt ? a : b);
      const leader = byFrame.get(newest.id)!;
      const offsets = group.memberOffsetsMeters;
      // Without measured offsets, bounded individual projection still gets
      // collision separation. A guessed coupling never manufactures a fix.
      if (offsets && offsets[newest.id] !== undefined) {
        for (const member of present) {
          const offset = offsets[member.id];
          if (offset !== undefined) desired.set(member.id,
            leader.renderedSMeters + offset - offsets[newest.id]!);
        }
      }
      active.add(group.id);
      let order = this.orders.get(group.id);
      if (!order) {
        order = present.map((vessel) => vessel.id).sort((a, b) =>
          desired.get(a)! - desired.get(b)! || a.localeCompare(b));
        this.orders.set(group.id, order);
      }
      const region = (member: PublicVesselSummary) => member.sMeters < RIVER_MOUTH_S_METERS ? member.corridor : member.sMeters < 0 ? 'mouth-span' : 'upriver';
      // If fresh evidence straddles a gate, its side outranks a historical
      // offset/order. Hulls can separate on either side without crossing it.
      const oneRegion = present.every((member) => region(member) === region(newest));
      order.forEach((id, index) => membership.set(id, { formation: oneRegion ? group.id : `${group.id}.${id}`, order: index, idle: leader.isIdle }));
    }
    for (const id of this.orders.keys()) if (!active.has(id)) this.orders.delete(id);

    // Place the freshest evidence first. Independently moored hulls already have
    // bank berths; they remain fixed obstacles for route-moving traffic.
    const sorted = [...frames].sort((a, b) =>
      Number(b.isIdle && !membership.has(b.id)) - Number(a.isIdle && !membership.has(a.id)) ||
      (byId.get(b.id)?.observedAt ?? "").localeCompare(byId.get(a.id)?.observedAt ?? "") || a.id.localeCompare(b.id));
    const positions = separateRouteHulls(sorted.flatMap((frame) => {
      const vessel = byId.get(frame.id);
      if (!vessel) return [];
      const berth = frame.isIdle && !membership.has(frame.id);
      const target = desired.get(frame.id)!;
      return [{ id: frame.id, sMeters: target,
        ...(berth ? { minimum: target, maximum: target } : displayRouteBounds(vessel)),
        radius: vesselDisplayRadius(vessel.type), ...membership.get(frame.id),
        pointAt: (s: number) => {
          const point = berth ? frame.rendered : routeLocation(vessel.corridor, s,
            projectedCorridorProgress(vessel, s)).point;
          return { x: point.x, y: point.z };
        } }];
    }), 4);
    return frames.map((frame) => {
      const vessel = byId.get(frame.id);
      const s = positions.get(frame.id);
      if (!vessel || s === undefined || (!membership.has(frame.id) && (frame.isIdle || s === frame.renderedSMeters))) return frame;
      const progress = projectedCorridorProgress(vessel, s);
      const location = routeLocation(vessel.corridor, s, progress);
      const along = inboundRouteTangent(location);
      const originalAlong = inboundRouteTangent(routeLocation(vessel.corridor, frame.renderedSMeters, frame.renderedCorridorProgress));
      const sign = frame.heading.x * originalAlong.x + frame.heading.z * originalAlong.z >= 0 ? 1 : -1;
      return { ...frame, rendered: location.point, renderedSMeters: s, renderedCorridorProgress: progress,
        tangent: location.tangent, heading: { x: along.x * sign, z: along.z * sign },
        isIdle: membership.get(frame.id)?.idle ?? frame.isIdle,
        isEstimated: frame.isEstimated || Math.abs(s - vessel.sMeters) > 0.1 };
    });
  }
}
